import React, { useState } from "react";
import { Wallet, CalendarRange, ListChecks, Loader2, Download } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const money = (n) => new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const ANIOS = ["2024", "2025", "2026", "2027"];

export default function PresupuestoReporte() {
  const [mes, setMes] = useState("7");
  const [anio, setAnio] = useState("2026");
  const [budgetId, setBudgetId] = useState("");
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [lineaSel, setLineaSel] = useState(null);
  const [disponible, setDisponible] = useState("");
  const openLinea = (r) => setLineaSel({ line_id: r.line_id, label: r.label, asignado: r.asignado });
  const exportExcel = async () => {
    const res = await api.get("/presupuestos/export", { params: { budget_id: budgetId }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(data?.budget?.name || "presupuesto").replace(/[\\/ ]+/g, "_")}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const { data: lista } = useQuery({
    queryKey: ["pres-list", mes, anio],
    queryFn: async () => {
      const d = (await api.get("/presupuestos/list", { params: { mes, anio } })).data;
      const buds = d.budgets || [];
      setBudgetId((prev) => (buds.some((b) => String(b.id) === prev) ? prev : (buds[0] ? String(buds[0].id) : "")));
      return d;
    },
  });
  const budgets = lista?.budgets || [];

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["pres-reporte", budgetId],
    queryFn: async () => (await api.get("/presupuestos/reporte", { params: { budget_id: budgetId } })).data,
    enabled: !!budgetId,
  });
  const { data: detalle, isLoading: detLoading } = useQuery({
    queryKey: ["pres-detalle", budgetId],
    queryFn: async () => (await api.get("/presupuestos/detalle", { params: { budget_id: budgetId } })).data,
    enabled: !!budgetId && detalleOpen,
  });
  const { data: lineaDet, isLoading: lineaLoading } = useQuery({
    queryKey: ["pres-detalle-linea", lineaSel?.line_id],
    queryFn: async () => (await api.get("/presupuestos/detalle_linea", { params: { line_id: lineaSel.line_id } })).data,
    enabled: !!lineaSel?.line_id,
  });

  const b = data?.budget;
  const N = b?.num_semanas || 5;
  const sems = Array.from({ length: N }, (_, i) => i);
  const cols = 1 + N + 5; // concepto + N semanas + prev + cent + asig + var + detalles

  const Row = ({ label, r, bold, cat }) => (
    <tr className={cat ? "bg-slate-800 text-white" : bold ? "bg-slate-100 font-bold" : "hover:bg-slate-50"} data-testid={cat ? `pres-cat-${label}` : undefined}>
      <td className={`px-3 py-1.5 text-left ${cat ? "font-bold uppercase tracking-wide" : ""}`}>{label}</td>
      {cat ? <td colSpan={cols - 1} /> : <>
        {sems.map((i) => <td key={i} className="px-3 py-1.5 text-right tabular-nums">{money(r.sem[i])}</td>)}
        <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{money(r.previsto)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{(r.cent || 0).toFixed(2)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{money(r.asignado)}</td>
        <td className={`px-3 py-1.5 text-right tabular-nums pres-var ${r.variacion > 0 ? "pres-var-pos" : r.variacion < 0 ? "pres-var-neg" : ""}`}>{money(r.variacion)}</td>
        <td className="px-3 py-1.5 text-center">
          {r.line_id ? (
            <button onClick={() => openLinea(r)} data-testid={`pres-row-detalle-${r.line_id}`}
              className="text-red-600 hover:text-red-700 underline text-xs font-semibold">Detalles</button>
          ) : null}
        </td>
      </>}
    </tr>
  );

  return (
    <div className="w-full space-y-6" data-testid="presupuesto-reporte">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Reportes de Presupuesto</p>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Reporte Presupuestal</h1>
        <p className="text-sm text-slate-500 mt-1">Presupuestos analíticos por semana · ZAE Odoo</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4" data-testid="pres-filters">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Mes</label>
            <Select value={mes} onValueChange={setMes}>
              <SelectTrigger className="h-10 mt-1 bg-slate-50 border-slate-200 text-sm" data-testid="pres-filter-mes"><SelectValue /></SelectTrigger>
              <SelectContent>{MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Año</label>
            <Select value={anio} onValueChange={setAnio}>
              <SelectTrigger className="h-10 mt-1 bg-slate-50 border-slate-200 text-sm" data-testid="pres-filter-anio"><SelectValue /></SelectTrigger>
              <SelectContent>{ANIOS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Presupuesto</label>
            <Select value={budgetId} onValueChange={setBudgetId}>
              <SelectTrigger className="h-10 mt-1 bg-slate-50 border-slate-200 text-sm" data-testid="pres-filter-budget">
                <SelectValue placeholder={budgets.length ? "Selecciona" : "Sin presupuestos"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {budgets.map((bd) => <SelectItem key={bd.id} value={String(bd.id)}>{bd.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Reporte */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="pres-report-card">
        {isLoading ? (
          <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div>
        ) : !b ? (
          <p className="text-slate-400 text-sm py-10 text-center">Selecciona un presupuesto para ver el reporte.</p>
        ) : (
          <>
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-red-600" />{b.name}
                  {isFetching && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                </h2>
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                  <CalendarRange className="w-3.5 h-3.5" />PERIODO: {b.date_from} a {b.date_to}
                </p>
                <p className="text-xs text-slate-500">COMPROMISO DE VENTA (KILOS): <span className="font-semibold tabular-nums">{money(b.compromiso_venta)}</span></p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Presupuesto disponible</label>
                  <input type="number" value={disponible} onChange={(e) => setDisponible(e.target.value)}
                    data-testid="pres-disponible-input" placeholder="0.00"
                    className="h-8 w-40 px-2 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-800 tabular-nums" />
                  {disponible !== "" && data?.grand_total && (
                    <span className="text-sm" data-testid="pres-restante">
                      Restante:{" "}
                      <span className={(parseFloat(disponible) - data.grand_total.asignado) >= 0 ? "pres-var-pos font-bold tabular-nums" : "pres-var-neg font-bold tabular-nums"}>
                        {money(parseFloat(disponible) - data.grand_total.asignado)}
                      </span>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={exportExcel} data-testid="pres-export-btn" variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50">
                  <Download className="w-4 h-4 mr-2" />Exportar a Excel
                </Button>
                <Button onClick={() => setDetalleOpen(true)} data-testid="pres-detalles-btn" className="bg-red-600 hover:bg-red-700 text-white">
                  <ListChecks className="w-4 h-4 mr-2" />Detalles
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-[13px] border-collapse" data-testid="pres-table">
                <thead>
                  <tr className="bg-slate-700 text-white">
                    <th className="px-3 py-2 text-left font-semibold">SEMANAS</th>
                    {sems.map((i) => <th key={i} className="px-3 py-2 text-right font-semibold">{i + 1}</th>)}
                    <th className="px-3 py-2 text-right font-semibold">Presupuesto autorizado</th>
                    <th className="px-3 py-2 text-right font-semibold">₵</th>
                    <th className="px-3 py-2 text-right font-semibold">Gasto ejercido (Asignado)</th>
                    <th className="px-3 py-2 text-right font-semibold">Presupuesto vs Gasto real</th>
                    <th className="px-3 py-2 text-center font-semibold">Detalles</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.groups || []).map((g) => (
                    <React.Fragment key={g.category}>
                      <Row label={g.category} cat />
                      {g.rows.map((r, idx) => <Row key={idx} label={r.label} r={r} />)}
                      <Row label={`TOTAL ${g.category}`} r={g.totals} bold />
                    </React.Fragment>
                  ))}
                  {data.grand_total && (
                    <tr className="bg-slate-900 text-white font-extrabold">
                      <td className="px-3 py-2 text-left">TOTAL GENERAL</td>
                      {sems.map((i) => <td key={i} className="px-3 py-2 text-right tabular-nums">{money(data.grand_total.sem[i])}</td>)}
                      <td className="px-3 py-2 text-right tabular-nums">{money(data.grand_total.previsto)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{(data.grand_total.cent || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(data.grand_total.asignado)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(data.grand_total.variacion)}</td>
                      <td className="px-3 py-2" />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Dialog open={detalleOpen} onOpenChange={setDetalleOpen}>
        <DialogContent className="max-w-2xl" data-testid="pres-detalle-modal">
          <DialogHeader><DialogTitle>Detalle de lo asignado</DialogTitle></DialogHeader>
          {detLoading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
          ) : (detalle?.items?.length ? (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-slate-100">
                  <tr><th className="px-3 py-2 text-left">Fecha</th><th className="px-3 py-2 text-left">Concepto</th><th className="px-3 py-2 text-right">Asignado</th></tr>
                </thead>
                <tbody>
                  {detalle.items.map((it, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`pres-detalle-row-${i}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap">{it.date || "—"}</td>
                      <td className="px-3 py-1.5">{it.description}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{money(it.committed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm py-8 text-center">Este presupuesto no tiene montos asignados registrados.</p>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={!!lineaSel} onOpenChange={(o) => !o && setLineaSel(null)}>
        <DialogContent className="max-w-2xl" data-testid="pres-detalle-linea-modal">
          <DialogHeader><DialogTitle>Asignado · {lineaSel?.label}</DialogTitle></DialogHeader>
          {lineaLoading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
          ) : (lineaDet?.items?.length ? (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-slate-100">
                  <tr><th className="px-3 py-2 text-left">Fecha</th><th className="px-3 py-2 text-left">Concepto</th><th className="px-3 py-2 text-right">Asignado</th></tr>
                </thead>
                <tbody>
                  {lineaDet.items.map((it, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`pres-linea-row-${i}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap">{(it.date || "").slice(0, 10) || "—"}</td>
                      <td className="px-3 py-1.5">{it.description}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{money(it.committed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm py-8 text-center">Esta línea no tiene montos asignados registrados.</p>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
